import React from 'react';
import { ArrowRight, ArrowLeft, Check, X, Target, Zap, TrendingUp, BarChart3, Users, DollarSign, Search, Video, Linkedin, Eye, MousePointer, LineChart, Building2, Briefcase, MapPin, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CookieBanner from '../components/layout/CookieBanner';

const whyAdsFail = [
  'They target people who are only "interested"',
  'Ads are built without real buyer intent',
  'Audiences are too broad',
  'Optimization is slow or inconsistent'
];

const intentBenefits = [
  'Ads reach people already in buying mode',
  'Higher conversion rates',
  'Lower cost per lead',
  'Shorter sales cycles',
  'Less wasted ad spend'
];

const adManagement = [
  'High-intent custom audience creation',
  'Pixel & conversion tracking setup',
  'Campaign structure & funnel strategy',
  'Scroll-stopping ad creatives',
  'High-converting ad copy',
  'A/B testing & creative iteration',
  'Daily optimization & scaling',
  'Clear performance reporting'
];

const platforms = [
  {
    name: 'Meta Ads',
    subtitle: 'Facebook & Instagram',
    description: 'Perfect for demand capture, retargeting, and scaling proven offers.',
    color: 'bg-blue-600'
  },
  {
    name: 'Google Ads',
    subtitle: 'Search & Display',
    description: 'Capture high-intent search traffic at the exact moment buyers are looking.',
    color: 'bg-red-500'
  },
  {
    name: 'TikTok Ads',
    subtitle: 'Video & Discovery',
    description: 'Reach new buyers with high-engagement creative and intent-driven targeting.',
    color: 'bg-black'
  },
  {
    name: 'YouTube Ads',
    subtitle: 'Video Marketing',
    description: 'Build trust and demand through video while targeting in-market audiences.',
    color: 'bg-red-600'
  },
  {
    name: 'LinkedIn Ads',
    subtitle: 'B2B & Professional',
    description: 'Ideal for B2B, decision-makers, and high-value services using intent-based targeting.',
    color: 'bg-blue-700'
  }
];

const process = [
  {
    step: '01',
    title: 'Intent & Market Analysis',
    description: 'We identify high-intent signals tied to your exact services or products.'
  },
  {
    step: '02',
    title: 'Custom Audience Creation',
    description: 'We build proprietary audiences based on real buying behavior — not guesses.'
  },
  {
    step: '03',
    title: 'Creative & Copy Development',
    description: 'We design ads that speak directly to buyers\' pain points and buying stage.'
  },
  {
    step: '04',
    title: 'Campaign Launch & Testing',
    description: 'We launch, test, and refine multiple ad angles for maximum performance.'
  },
  {
    step: '05',
    title: 'Optimization & Scaling',
    description: 'We continuously optimize bids, creatives, and audiences to lower CPL and increase volume.'
  }
];

const results = [
  'Lower cost per lead',
  'Higher quality leads',
  'Improved conversion rates',
  'More predictable ad performance',
  'Ads that scale profitably'
];

const whoIsFor = [
  { icon: Briefcase, title: 'Service-based businesses' },
  { icon: Building2, title: 'B2B & SaaS companies' },
  { icon: DollarSign, title: 'High-ticket offers' },
  { icon: MapPin, title: 'Local & national brands' },
  { icon: Sparkles, title: 'Companies tired of wasting ad spend' }
];

const DigitalAds = () => {
  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex flex-col justify-center overflow-hidden bg-[#f8f7f4] pt-32 pb-16">
          {/* Gradient blob background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-0 right-0 w-[80%] h-[120%] opacity-50"
              style={{
                background: `
                  radial-gradient(ellipse 70% 50% at 75% 20%, rgba(59, 130, 246, 0.3) 0%, transparent 50%),
                  radial-gradient(ellipse 60% 50% at 85% 50%, rgba(16, 185, 129, 0.25) 0%, transparent 50%),
                  radial-gradient(ellipse 50% 60% at 65% 70%, rgba(245, 158, 11, 0.2) 0%, transparent 50%)
                `,
                filter: 'blur(60px)',
                transform: 'rotate(-10deg)'
              }}
            />
          </div>

          <div className="relative z-10 px-6 md:px-12 lg:px-24">
            {/* Back link */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-12"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>

            <div className="max-w-4xl">
              <span className="text-sm uppercase tracking-widest text-gray-500 mb-4 block">Digital Advertising</span>
              <h1 className="text-[clamp(2.5rem,5vw,4.5rem)] font-light leading-[1.1] text-black mb-6">
                Digital Advertising That Targets{' '}
                <span className="font-medium">Buyers — Not Browsers</span>
              </h1>
              <p className="text-xl text-gray-700 max-w-2xl mb-4">
                Ads Powered by Exclusive High-Intent Data
              </p>
              <p className="text-lg text-gray-600 max-w-2xl mb-8">
                We don't guess who might buy. We target people actively searching for what you sell — right now. 
                Most ad agencies rely on broad interests and outdated targeting. We use exclusive high-intent data 
                to identify real buyers already in-market, then build and manage winning ad campaigns that convert at a lower cost per lead.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
                >
                  Get a Free Ad Strategy Call <ArrowRight size={18} />
                </a>
                <a
                  href="#intent-data"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-black text-black rounded-full font-medium hover:bg-black hover:text-white transition-colors"
                >
                  See How Our Intent Data Works
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why Ads Fail */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium mb-6">
                  Why Most Digital Ads <span className="text-red-400">Fail</span>
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  Most digital ad campaigns struggle because:
                </p>
                <ul className="space-y-4">
                  {whyAdsFail.map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <X size={18} className="text-red-400 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-lg text-white mt-8 font-medium">
                  That's why cost per lead keeps rising — and results stay unpredictable.
                </p>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-3xl p-8">
                  <div className="space-y-4">
                    <div className="bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400">Cost Per Lead</span>
                        <span className="text-red-400">+47%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="w-3/4 h-full bg-red-500 rounded-full"></div>
                      </div>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400">Conversion Rate</span>
                        <span className="text-red-400">-23%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="w-1/3 h-full bg-red-500 rounded-full"></div>
                      </div>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-400">Wasted Ad Spend</span>
                        <span className="text-red-400">+62%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className="w-4/5 h-full bg-red-500 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Advantage - Intent Data */}
        <section id="intent-data" className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-sm uppercase tracking-widest text-green-600 mb-4 block">Our Advantage</span>
                <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                  We Target People <span className="text-green-600">Actively Searching</span>
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Our proprietary high-intent data identifies individuals and businesses actively researching, 
                  comparing, or searching for specific services and products.
                </p>
                <div className="mb-8">
                  <p className="text-gray-700 font-medium mb-4">What this means for you:</p>
                  <ul className="space-y-3">
                    {intentBenefits.map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check size={18} className="text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-lg text-black font-medium">
                  Instead of shouting into the void, your ads show up exactly when buyers are ready.
                </p>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-3xl p-8">
                  <div className="space-y-4">
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Cost Per Lead</span>
                        <span className="text-green-600 font-medium">-62%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="w-1/3 h-full bg-green-500 rounded-full"></div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Conversion Rate</span>
                        <span className="text-green-600 font-medium">+340%</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="w-4/5 h-full bg-green-500 rounded-full"></div>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600">Return on Ad Spend</span>
                        <span className="text-green-600 font-medium">5.8x</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="w-3/4 h-full bg-green-500 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Done-For-You Ad Management */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Done-For-You Digital Advertising
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                We Handle Everything — Start to Scale
              </p>
              <p className="text-gray-600">
                You don't need multiple vendors or internal ad experts. We fully manage your advertising 
                from strategy to optimization.
              </p>
            </div>

            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm">
              <p className="text-gray-700 font-medium mb-6">Our digital ad management includes:</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {adManagement.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
                    <Check size={18} className="text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-lg text-black font-medium mt-8 text-center">
                You focus on closing deals. We handle the rest.
              </p>
            </div>
          </div>
        </section>

        {/* Platforms */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-[clamp(3rem,8vw,8rem)] font-light text-gray-300 leading-none tracking-tight">
                <span className="block">AD</span>
                <span className="block ml-[10%]">PLATFORMS</span>
              </h2>
              <p className="text-lg text-gray-600 mt-8">
                We create and manage ads across all major platforms — each with platform-specific strategies.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {platforms.map((platform, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-md transition-all group">
                  <div className={`w-12 h-12 ${platform.color} rounded-xl flex items-center justify-center text-white font-bold mb-4`}>
                    {platform.name.charAt(0)}
                  </div>
                  <h3 className="text-xl font-medium text-black mb-1">{platform.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">{platform.subtitle}</p>
                  <p className="text-gray-600 leading-relaxed">{platform.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium mb-6">
                How Our Digital Ad Process Works
              </h2>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {process.map((item, index) => (
                <div key={index} className="relative">
                  <span className="text-6xl font-light text-gray-700">{item.step}</span>
                  <h3 className="text-lg font-medium text-white mt-2 mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-8 right-0 w-full h-px bg-gray-700 -mr-4"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-12">
                Results You Can Expect
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.map((result, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check size={20} className="text-green-600" />
                    </div>
                    <p className="text-gray-800 font-medium text-left">{result}</p>
                  </div>
                ))}
              </div>
              <p className="text-lg text-gray-700 mt-12">
                This is digital advertising built for growth — not vanity metrics.
              </p>
            </div>
          </div>
        </section>

        {/* Who Is This For */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Who This Is For
              </h2>
              <p className="text-lg text-gray-600">
                Our digital advertising is ideal for:
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
              {whoIsFor.map((item, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-6 text-center hover:shadow-md transition-shadow">
                  <item.icon className="w-10 h-10 text-gray-700 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-black">{item.title}</h3>
                </div>
              ))}
            </div>

            <p className="text-center text-lg text-gray-700 font-medium">
              If you want better leads, not just more impressions — this is for you.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-light mb-6">
                Ready to Advertise to Buyers{' '}
                <span className="font-medium">Who Are Already Searching?</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Stop guessing. Start targeting people ready to buy.
              </p>
              <a
                href="mailto:hello@gomobyle.com"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors mb-4"
              >
                Book a Free Digital Ad Strategy Call <ArrowRight size={18} />
              </a>
              <p className="text-gray-500 text-sm">
                No contracts. No wasted spend. Just smarter advertising.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default DigitalAds;
